import { useEffect, useMemo, useState } from 'react'
import { useIsMounted } from '../utils/ReactUtils'
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table'
import { Grid, Skeleton, IconButton } from '@mui/material'
import { Edit, Delete } from '@mui/icons-material'
import { getRequestConfig } from '../auth/auth.js'
import { ApiGateway } from '../config/config.js';
import axios from 'axios'
import AttributeDefinitionDialog from './AttributeDefinitionDialog.js'
import { default as bus } from '../utils/EventBus'
import Event from '../utils/EventNames.js'
import ConfirmDeleteDialog from "./ConfirmDeleteDialog.js";

export default function ListExtendedAttributes(props = { minimal: false }) {
  const isMounted = useIsMounted()

  const [dataRows, setDataRows] = useState([])
  const [addDialogOpen, setAddDialogOpen] = useState(false)
  const [currentRow, setCurrentRow] = useState()
  const [confirmDeleteDialogOpen, setConfirmDeleteDialogOpen] = useState('');
  const [confirmDeleteDialogProps, setConfirmDeleteDialogProps] = useState({});

  const openAddDialog = () => {
    console.log('open dialog')
    setAddDialogOpen(true)
  }

  useEffect(() => {
    const eventName = 'add-extended-attribute'
    bus.addListener(eventName, openAddDialog)
    return () => {
      bus.removeListener(eventName, openAddDialog)
    }
  }, [])

  const submitAddRecord = async (values, isEdit) => {
    props.startLoading()
    try {
      const requestConfig = await getRequestConfig()
      if(isEdit) {
        const basePath = `${ApiGateway.attribute}/definitions/${values.id}`
        await axios.put(basePath, values, requestConfig)
      } else {
        const basePath = `${ApiGateway.attribute}/definitions`
        await axios.post(basePath, values, requestConfig)
      }
    } catch(err) {
      bus.emit(Event.ALERT, {
        "text": err?.response?.data || err.message,
        "style": "error",
        "duration": 3000
      })
    }

    setAddDialogOpen(false)
    props.stopLoading()
    props.refresh()
  }

  const deleteRecord = async (id) => {
    props.startLoading()
    try {
      const requestConfig = await getRequestConfig()
      const basePath = `${ApiGateway.attribute}/definitions/${id}`
      await axios.delete(basePath, requestConfig)
      setAddDialogOpen(false)
    } catch(err) {
      bus.emit(Event.ALERT, {
        "text": err?.response?.data || err.message,
        "style": "error",
        "duration": 3000
      })
    }
    props.stopLoading()
    props.refresh()
  }

  const flexProps = {
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: 0
  }
  const colProps = {
    size: 120,
    maxSize: 200,
    minSize: 40
  }
  const listColumns = useMemo(() => [
    {
      accessorKey: 'entityType',
      header: 'Entity Type',
      editable: false,
      ...colProps,
      // size: undefined,
      filterVariant: 'multi-select',
      filterSelectOptions: ['ownership', 'facility'],
      muiFilterTextFieldProps: { placeholder: 'Select...' },
      muiTableHeadCellProps: {
        align: 'left',
        sx: { ...flexProps }
      },
      muiTableBodyCellProps: {
        align: 'left',
        sx: { ...flexProps }
      },
    },
    {
      accessorKey: 'name',
      header: 'Name',
      // grow: 1,
      ...colProps,
      editable: false,
      filterVariant: 'text',
      muiFilterTextFieldProps: { placeholder: 'Filter by name' },
      muiTableHeadCellProps: {
        align: 'left',
        sx: { ...flexProps }
      },
      muiTableBodyCellProps: {
        align: 'left',
        sx: { ...flexProps }
      },
    },
    {
      accessorKey: 'label',
      header: 'Label',
      // grow: 1,
      ...colProps,
      editable: false,
      filterVariant: 'text',
      muiFilterTextFieldProps: { placeholder: 'Filter by label' },
      muiTableHeadCellProps: {
        align: 'left',
        sx: { ...flexProps }
      },
      muiTableBodyCellProps: {
        align: 'left',
        sx: { ...flexProps }
      },
    },
    {
      accessorKey: 'dataType',
      header: 'Data Type',
      // grow: 1,
      ...colProps,
      editable: false,
      filterVariant: 'multi-select',
      filterSelectOptions: [
        'string',
        'integer',
        'decimal',
        'boolean',
        'date',
        'datetime',
        'json',
      ],
      muiFilterTextFieldProps: { placeholder: 'Select...' },
      muiTableHeadCellProps: {
        align: 'left',
        sx: { ...flexProps }
      },
      muiTableBodyCellProps: {
        align: 'center',
        sx: { ...flexProps }
      },
    },
    {
      accessorKey: 'defaultValue',
      header: 'Default Value',
      // grow: 1,
      ...colProps,
      editable: false,
      filterVariant: 'text',
      muiFilterTextFieldProps: { placeholder: 'Filter by default value' },
      muiTableHeadCellProps: {
        align: 'left',
        sx: { ...flexProps }
      },
      muiTableBodyCellProps: {
        align: 'left',
        sx: { ...flexProps }
      },
      Cell: ({ row }) => {
        const val = row.original.defaultValue;
        return val ?? '';
      },
    },
    {
      accessorKey: 'isRequired',
      header: 'Required',
      // grow: 1,
      ...colProps,
      editable: false,
      filterVariant: 'checkbox',
      muiTableHeadCellProps: {
        align: 'center',
        sx: { ...flexProps }
      },
      muiTableBodyCellProps: {
        align: 'center',
        sx: { ...flexProps }
      },
      Cell: ({ row }) => (row.original.isRequired ? 'Yes' : 'No'),
    },
    {
      accessorKey: 'shareValues',
      header: 'Shared Values',
      // grow: 1,
      ...colProps,
      editable: false,
      filterVariant: 'checkbox',
      muiTableHeadCellProps: {
        align: 'center',
        sx: { ...flexProps }
      },
      muiTableBodyCellProps: {
        align: 'center',
        sx: { ...flexProps }
      },
      Cell: ({ row }) => (row.original.shareValues ? 'Yes' : 'No'),
    },
    {
      accessorKey: 'visible',
      header: 'Visible',
      // grow: 1,
      ...colProps,
      editable: false,
      filterVariant: 'checkbox',
      muiTableHeadCellProps: {
        align: 'center',
        sx: { ...flexProps }
      },
      muiTableBodyCellProps: {
        align: 'center',
        sx: { ...flexProps }
      },
      Cell: ({ row }) => (row.original.visible ? 'Yes' : 'No'),
    },
    // ---- New Actions Column ----
    {
      id: 'actions',
      header: 'Actions',
      // grow: 1,
      ...colProps,
      enableColumnFilter: false,
      enableSorting: false,
      layoutMode: 'grid',
      columnResizeMode: 'fit',
      muiTableHeadCellProps: {
        align: 'center',
        sx: { ...flexProps }
      },
      muiTableBodyCellProps: {
        align: 'center',
        sx: { ...flexProps }
      },
      Cell: ({ row }) => {
        const handleEdit = () => {
          setCurrentRow(row.original)
          setAddDialogOpen(true)
        };

        const handleDelete = () => {
          setConfirmDeleteDialogProps({
            onConfirm: () => { 
              deleteRecord(row.original.id)
              setCurrentRow()
            },
            itemName: row.original.label,
            onClose: () => setConfirmDeleteDialogOpen(false)
          })
          setConfirmDeleteDialogOpen(true)
        };

        return (
          <>
            <IconButton
              size="small"
              color="primary"
              onClick={handleEdit}
            >
              <Edit fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              color="error"
              onClick={handleDelete}
            >
              <Delete fontSize="small" />
            </IconButton>
          </>
        );
      },
    }
  ], []);

  const getAttributeDefinitionList = async () => {
    props.startLoading()

    const requestConfig = await getRequestConfig()
    const basePath = `${ApiGateway.attribute}/definitions/search`

    const search = {}

    const response = await axios.post(basePath, search, requestConfig)
    setDataRows(response.data?.results || [])

    props.stopLoading()
  }

  useEffect(() => {
    if (isMounted()) {
      getAttributeDefinitionList()
    }
  }, [])

  const table = useMaterialReactTable({
    layoutMode: 'semantic',
    columnFilterModeOptions: [
      'fuzzy',
      'greaterThan',
      'lessThan'
    ],
    columns: listColumns,
    data: dataRows,
    enableColumnActions: false,
    enableColumnFilters: props.showFilters && !props.minimal,
    initialState: {
      showColumnFilters: !props.minimal,
      sorting: [{ id: 'manufacturerName', desc: false }],
      pagination: (props.minimal === true) ? undefined : {
        pageIndex: props.pageIndex ?? 0,
        pageSize: props.rowsPerPage ?? 15
      }
    },
    enablePagination: (props.minimal !== true),
    enableSorting: true,
    enableBottomToolbar: true,
    enableEditing: false,
    muiTableContainerProps: (props.minimal === true) ? { width: '100%' } : {
      sx: {
        minHeight: 300,
        width: '100%',
      }
    },
    muiTableBodyCellProps: {
      sx: {
        fontSize: '.82rem',
        padding: '4px !important',
        sx: { flex: 1, minWidth: 0 }
      }
    },
    renderTopToolbar: false,
  });

  return (
    <>
      <AttributeDefinitionDialog
        open={addDialogOpen}
        onClose={() => {
          setAddDialogOpen(false)
          setCurrentRow()
        }}
        onSubmit={submitAddRecord}
        editData={currentRow}
      />
      <ConfirmDeleteDialog
        open={confirmDeleteDialogOpen}
        {...confirmDeleteDialogProps}
      />
      <Grid container rowSpacing={2} >
        <Grid item xs={12}>
          {props.loading && <Skeleton variant='rounded' animation='wave' height={100} sx={{ margin: '5px' }} />}
          {!props.loading && <MaterialReactTable table={table} />}
        </Grid>
      </Grid>
    </>
  )

}